import Router from "@/helpers/router";

import {
  WHATS_NEW_AUDIENCES,
  WHATS_NEW_ICONS,
  WhatsNewEntryI,
} from "../lib/types";

const { CALLER, POD_LEAD, LGA, ADMIN } = WHATS_NEW_AUDIENCES;

/**
 * The block-system changes of C-14 and C-12 wave 2 (live in prod from
 * release 11.5.80, backend e6b064245), announced before anything else in
 * C-16 is built (Omar, 2026-10-25). Live from the release through Tuesday
 * 2026-10-27 ET, the first two days strikes count (C16-D311).
 *
 * Facts: knowledge platform-engineering/user-app-roles.md, block-system.md,
 * tasks/C-12-A-*, C-12-B-*, the C-12 wave 2 and C-14 W3 notes. Strikes:
 * common/dao/core/model_dial_block_strikes.go (fumble, or a no-show with no
 * dials; 30 days; flags only, C14 D67). Omar may reword any line.
 */
export const BLOCK_SYSTEM_2026_10_26: WhatsNewEntryI = {
  id: "2026-10-26-block-system",
  startsAt: "2026-10-25T04:00:00.000Z",
  endsAt: "2026-10-28T04:00:00.000Z",
  title: {
    [CALLER]: "Updates to your blocks",
    [POD_LEAD]: "Updates for you and your pod",
    [LGA]: "Updates to your Team pages",
    [ADMIN]: "Updates to the Team pages",
  },
  intro: {
    [CALLER]:
      "A few changes to dial blocks are live. Here's what's different for you.",
    [POD_LEAD]:
      "Changes to your own blocks, plus new views of your pod under Team.",
    [LGA]: "Here's what moved and what's new under Team.",
    [ADMIN]:
      "Here's what moved and what's new under Team, for every workspace.",
  },
  sections: [
    // Callers, and Pod Leads (who are callers too).
    {
      id: "my-week",
      audiences: [CALLER, POD_LEAD],
      icon: WHATS_NEW_ICONS.CALENDAR,
      title: "My Week",
      items: [
        "Change your usual commitment: in “Update availability”, pick “My usual commitment” to ask your LGA for a new number of blocks a week (1 to 24). Once approved, it starts the next week.",
        "Ask again for the same week: if your LGA already answered a week's update, you can send another. Their earlier answer stays until they reply.",
      ],
      link: { label: "Open My Week", href: Router.myWeek() },
    },
    {
      id: "strikes",
      audiences: [CALLER, POD_LEAD],
      icon: WHATS_NEW_ICONS.STRIKE,
      title: "Strikes count from Monday, Oct 26",
      items: [
        "A block that hits none of its goals (dials, pitches or a meeting), or a booked block with no dials at all, counts as a strike.",
        "A strike lasts 30 days. Your LGA and Pod Lead see your strikes and get an alert at 3. Your LGA can excuse one.",
        "For now strikes are only flags: you can still book as usual.",
      ],
    },
    {
      id: "score",
      audiences: [CALLER, POD_LEAD],
      icon: WHATS_NEW_ICONS.SCORE,
      title: "Your RUI score",
      items: [
        "Your whole day counts: every dial, pitch and meeting until 9 PM ET goes to your nearest block, on any campaign.",
        "“How your RUI score works” on My Week has the details.",
      ],
    },

    // Pod Leads.
    {
      id: "pod",
      audiences: [POD_LEAD],
      icon: WHATS_NEW_ICONS.TEAM,
      title: "Your pod, under Team",
      items: [
        "Calendar: your pod's week, read-only.",
        "Health: Rep Health for your pod and Campaign Health for its workspace, read-only.",
        "Leaderboard: your reps, and how the pods compare.",
        "Bells: one when someone in your pod reaches 3 strikes, and one when they send a request.",
      ],
      link: { label: "Open Team Calendar", href: Router.manageCalendar() },
    },

    // LGAs and Admins.
    {
      id: "calendar",
      audiences: [LGA, ADMIN],
      icon: WHATS_NEW_ICONS.CALENDAR,
      title: "Calendar",
      items: [
        "Capacity now holds each campaign's settings: Edit and Activity sit on every campaign row. The Campaigns page moved here.",
        "Requests lists one-week updates and usual commitment changes together. A repeat request shows your earlier answer, and a bell tells you when one is waiting.",
      ],
      link: { label: "Open Calendar", href: Router.manageCalendar() },
    },
    {
      id: "health",
      audiences: [LGA, ADMIN],
      icon: WHATS_NEW_ICONS.HEALTH,
      title: "Health",
      items: [
        "Rep Health, Campaign Health and Data Health are now tabs on one page. Data Health moved here from Analytics.",
        "Reset score sets a caller back to 85, or use Reset everyone. The history is kept.",
      ],
      link: { label: "Open Health", href: Router.manageHealth() },
    },
    {
      id: "team-strikes",
      audiences: [LGA, ADMIN],
      icon: WHATS_NEW_ICONS.STRIKE,
      title: "Strikes count from Monday, Oct 26",
      items: [
        "A strike is a block that hits none of its goals, or a booked block with no dials at all. It lasts 30 days.",
        "See a caller's strikes in Rep Health and excuse one with a reason. You get a bell when a caller reaches 3.",
        "Strikes are flags only: nobody's booking is paused.",
      ],
    },
    {
      id: "reps-leaderboard",
      audiences: [LGA, ADMIN],
      icon: WHATS_NEW_ICONS.TROPHY,
      title: "Reps and Leaderboard",
      items: [
        "Reps now looks like the Leaderboard. Click a rep to see their campaigns.",
        "The Leaderboard has a new LGAs tab, so you can see how you compare.",
      ],
      link: { label: "Open Leaderboard", href: Router.manageLeaderboard() },
    },
    {
      id: "pods",
      audiences: [LGA, ADMIN],
      icon: WHATS_NEW_ICONS.TEAM,
      title: "Pods and Pod Leads",
      items: [
        "Group callers into pods under Team → Pods and pick a Pod Lead.",
        "A Pod Lead sees their pod's Calendar, Health and Leaderboard, read-only.",
      ],
    },

    // Admins only.
    {
      id: "admin",
      audiences: [ADMIN],
      icon: WHATS_NEW_ICONS.SHIELD,
      title: "Admin only",
      items: [
        "Activity: every change made on the Team pages, who made it and when.",
        "Admin → Roles: give or remove Admin, LGA and Pod Lead.",
        "Calendar and Health open any workspace, and Calendar has an All workspaces view.",
      ],
      link: { label: "Open Activity", href: Router.manageActivity() },
    },
  ],
};
